import { Injectable } from '@angular/core';
import { RecurrenceDto } from '@interfaces/recurrence';
import { Announcement } from '@models/announcement';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ByWeekday, RRule, Weekday } from 'rrule';
import { Recurrence } from '@models/recurrence';
import { EventFormValues } from 'src/app/manage/activities/event-form-modal-component/event-form-modal.component';
import { EventTimingFormValues } from 'src/app/manage/activities/event-form-modal-component/forms/event-timing/event-timing.component';
import isEqual from 'lodash/isEqual';
import { addDays, format, parseISO, startOfDay } from 'date-fns';
import { lastValueFrom } from 'rxjs';
import { zonedTimeToUtc } from 'date-fns-tz';
import { TimezoneService } from './timezone.service';
import {
  RecurrenceEditPayload,
  RecurrenceEditResponse,
} from 'src/app/core/interfaces/recurrence-edit';
@Injectable({
  providedIn: 'root',
})
export class RecurrenceService {
  constructor(
    private http: HttpClient,
    private timezoneService: TimezoneService,
  ) {}

  // Print human-readable version of rrule
  public prettyPrintRecurrenceRecipe(rule: Partial<RecurrenceDto>): string {
    if (!rule) {
      return '';
    }
    let readableRule = '';

    // Choose daily, weekly monthly
    // monthly on x day
    // @ts-ignore
    if (rule?.freq === RRule.MONTHLY) {
      readableRule = 'month';
    } else if (
      rule.byweekday &&
      (rule.byweekday as Array<ByWeekday>).length > 0
    ) {
      // weekly on [x] days
      readableRule = 'week';
    } else {
      readableRule = 'day';
    }
    if (rule.interval) {
      switch (rule.interval) {
        case 1:
          readableRule = `every ${readableRule}`;
          break;
        case 2:
          readableRule = `every other ${readableRule}`;
          break;
        default:
          readableRule = `every ${rule.interval} ${readableRule}s`;
      }
    }

    // we start the week on Sunday (given index 0)
    if (rule.byweekday && (rule.byweekday as Array<ByWeekday>).length > 0) {
      let days = '';
      let datePosition = '';
      let options = rule.byweekday;
      if (typeof rule.byweekday === 'string') {
        options = [rule.byweekday];
      }
      (options as Array<Weekday>).forEach((day: Weekday | string, index) => {
        let dayString = '';
        let switchValue;
        if ((day as Weekday)?.weekday) {
          switchValue = (day as Weekday).weekday;
        } else if (isNaN(Number(day))) {
          switchValue = this.getWeekdayIntegerFromString(day as string);
        } else {
          switchValue = Number(day);
        }
        switch (switchValue) {
          case RRule.MO.weekday:
            // Sunday
            dayString = `Monday`;
            break;
          case RRule.TU.weekday:
            // Monday
            dayString = `Tuesday`;
            break;
          case RRule.WE.weekday:
            // Tuesday
            dayString = `Wednesday`;
            break;
          case RRule.TH.weekday:
            // Wednesday
            dayString = `Thursday`;
            break;
          case RRule.FR.weekday:
            // Thursday
            dayString = `Friday`;
            break;
          case RRule.SA.weekday:
            // Friday
            dayString = `Saturday`;
            break;
          case RRule.SU.weekday:
            // Saturday
            dayString = `Sunday`;
            break;
        }

        if (rule?.freq === RRule.MONTHLY) {
          switch (rule.nbyweekday) {
            case 1:
              datePosition = `first`;
              break;
            case 2:
              datePosition = `second`;
              break;
            case 3:
              datePosition = `third`;
              break;
            case 4:
              datePosition = `fourth`;
              break;
            case -1:
              datePosition = `last`;
              break;
            case -2:
              datePosition = `second to last`;
              break;
            case -3:
              datePosition = `third to last`;
              break;
            case -4:
              datePosition = `fourth to last`;
              break;
          }
        }

        if (days.length === 0) {
          days = dayString;
        } else if (index === (rule.byweekday as Array<ByWeekday>).length - 1) {
          // there are more than one days and this is the last item
          days = `${days} and ${dayString}`;
        } else {
          // there are more than one days and this is NOT the last item
          days = `${days}, ${dayString}`;
        }
      });

      readableRule = `${readableRule} on ${
        datePosition.length > 0 ? `the ${datePosition} ` : ''
      }${days}`;
    }

    if (rule.count) {
      readableRule = `${readableRule} until it repeats ${rule.count} times`;
    } else if (rule.until) {
      readableRule = `${readableRule} until ${this.dateToTimezoneString(
        rule.until,
        rule.tzid,
      )}`;
    }
    if (rule?.exdates?.length > 0) {
      let exceptions = '';
      rule.exdates.forEach((date, index) => {
        if (index === 0) {
          exceptions = `${this.dateToTimezoneString(date, rule.tzid)}`;
        } else if (index === rule.exdates.length - 1) {
          exceptions = `${exceptions} and ${this.dateToTimezoneString(
            date,
            rule.tzid,
          )}`;
        } else {
          exceptions = `${exceptions}, ${this.dateToTimezoneString(
            date,
            rule.tzid,
          )}`;
        }
      });
      readableRule = `${readableRule} (except on ${exceptions})`;
    }
    // Until?
    return readableRule;
  }

  public dateToTimezoneString(date: Date | string, timezone?: string) {
    if (timezone) {
      return this.timezoneService.formatDateInTimeZone(
        date,
        timezone,
        'M-d-yy',
      );
    }
    if (typeof date === 'string') {
      const parsedDate = parseISO(date as string);
      return format(parsedDate, 'M-d-yy');
    }
    return format(new Date(date), 'M-d-yy');
  }

  // converts stored time values on activities to recurrence-friendly timezone strings
  public dateFromActivity(activity) {
    return format(
      zonedTimeToUtc(activity.eventStart, activity.site.timezone),
      'yyyy-MM-dd',
    );
  }

  // Date MUST BE in timezone-friendly UTC string
  public deleteThisAndFutureRecurringAnnouncements(announcement: Announcement) {
    const url = `${environment.apiv3Url}/recurrence/announcements/${
      announcement.site.id
    }/recurrence/${announcement.recurrence.id}/delete-future/${new Date(
      announcement.eventStart,
    ).getTime()}`;
    try {
      return <Promise<any>>(
        lastValueFrom(this.http.post(url, {}, { responseType: 'text' }))
      );
    } catch (err) {
      console.error('ERROR', err);
      return;
    }
  }

  // Gets all announcements by recurrence ID
  getRecurringAnnouncementsByAnnouncement(
    announcement: Announcement,
  ): Promise<Array<Announcement>> {
    const url = `${environment.apiv3Url}/announcements/${announcement.site.id}/recurrence/${announcement.recurrence.id}`;
    try {
      return <Promise<any>>lastValueFrom(this.http.get(url));
    } catch (err) {
      console.error('ERROR', err);
      return;
    }
  }

  public async editRecurringAnnouncementSeries(
    payload: RecurrenceEditPayload,
  ): Promise<RecurrenceEditResponse> {
    const url = `${environment.apiv3Url}/announcements/${payload.announcementId}/recurrence-edit`;
    try {
      return await lastValueFrom(
        this.http.post<RecurrenceEditResponse>(url, payload),
      );
    } catch (err) {
      console.error('ERROR', err);
      throw err;
    }
  }

  // Helper method to ensure byweekday is always an array of strings
  private ensureByweekdayAsStrings(recurrence: any): any {
    if (recurrence?.byweekday && Array.isArray(recurrence.byweekday)) {
      return {
        ...recurrence,
        byweekday: recurrence.byweekday.map((day) =>
          typeof day === 'number' ? String(day) : day,
        ),
      };
    }
    return recurrence;
  }

  saveRecurrence(rule: RecurrenceDto) {
    const url = `${environment.apiv3Url}/recurrence`;

    // Ensure byweekday is an array of strings
    const body = this.ensureByweekdayAsStrings(rule);

    try {
      return <Promise<Recurrence>>lastValueFrom(
        this.http.put(url, JSON.stringify(body), {
          headers: { 'Content-Type': 'application/json' },
        }),
      );
    } catch (err) {
      console.error('ERROR', err);
      return;
    }
  }

  deleteRecurrence(announcement) {
    const url = `${environment.apiv3Url}/recurrence/${announcement.recurrence.id}`;

    try {
      return lastValueFrom(this.http.delete(url));
    } catch (err) {
      console.error('ERROR', err);
      return;
    }
  }

  public async updateUntilFromActivity(announcement: Announcement) {
    /**
     * Deleting this and all future instances of this announcement:
     * update the event recurrence (at id) with new 'until' date, delete this and future announcements
     */
    const endOfThePreviousDay = format(
      addDays(
        zonedTimeToUtc(announcement.eventStart, announcement.site.timezone),
        -1,
      ),
      'yyyy-MM-dd',
    );
    let start = announcement.recurrence.dtstart;
    if (start instanceof Date) {
      start = format(start, 'yyyy-MM-dd');
    } else if (typeof start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(start)) {
      // For date-only strings (YYYY-MM-DD), keep them as-is to avoid timezone shifts
      start = start;
    } else if (typeof start === 'string') {
      // For other string formats, parse carefully
      start = format(parseISO(start), 'yyyy-MM-dd');
    }

    let weekdays;
    if (
      announcement?.recurrence?.byweekday &&
      typeof announcement?.recurrence?.byweekday[0] !== 'string'
    ) {
      weekdays = announcement?.recurrence?.byweekday.map((day) =>
        this.getWeekdayString(day),
      );
    }
    /**
     * Update recurrence with new 'until' date. If there was previously a count value, null and replace.
     */

    return await this.saveRecurrence(
      this.ensureByweekdayAsStrings({
        ...announcement.recurrence,
        dtstart: start,
        count: null, // if it was a count before, it's and until now
        until: endOfThePreviousDay,
      }),
    );
  }

  public newRecurrenceModelFromActivity(announcement: Announcement) {
    return new Recurrence({
      ...announcement.recurrence,
      tzid: announcement.site.timezone,
    });
  }

  public async saveNewRecurrenceFromActivity(
    announcement: Announcement,
    exdates?: string[],
  ): Promise<Recurrence> {
    /**
     * Update recurrence with new 'start' date. Delete unneeded properties.
     */
    const recurrenceToCopy = {
      ...announcement.recurrence,
      exdates: exdates ? exdates : null, // if exdates are not explicitly provided, they are nullified
    };
    delete recurrenceToCopy.createdOn;
    delete recurrenceToCopy.updatedOn;
    delete recurrenceToCopy.id;
    return this.saveRecurrence(
      this.ensureByweekdayAsStrings({
        ...recurrenceToCopy,
      }),
    );
  }

  finalizeRecurrenceDetailsToSave(timing, exdatesToMerge?, timezone?: string) {
    let exdates;
    if (timing?.recurrence?.exdates || exdatesToMerge) {
      exdates = exdatesToMerge;
    }
    let byweekday =
      typeof timing?.recurrenceRecipe?.byweekday === 'string'
        ? [timing?.recurrenceRecipe?.byweekday]
        : timing?.recurrenceRecipe?.byweekday;

    if (byweekday?.length > 0 && typeof byweekday[0] !== 'string') {
      byweekday = byweekday.map((value) => {
        return value?.weekday;
      });
    }

    const start = this.getDtstartFromFormValues(timing, timezone);
    const until =
      timing?.recurrenceRecipe?.until instanceof Date
        ? format(timing?.recurrenceRecipe?.until, 'yyyy-MM-dd')
        : timing?.recurrenceRecipe?.until;
    const recurrence = {
      ...timing.recurrenceRecipe,
      byweekday: byweekday ? byweekday : null,
      dtstart: start,
      until: until ? until : null,
      exdates: exdates ? exdates : null,
    };
    if (recurrence.stopCondition) {
      delete recurrence.stopCondition;
    }

    // Ensure byweekday is an array of strings
    return this.ensureByweekdayAsStrings(recurrence);
  }

  public getDtstartFromFormValues(
    form: EventTimingFormValues,
    timezone?: string,
  ): string {
    if (form?.date instanceof Date) {
      if (timezone) {
        // For timezone-aware processing, format the date in the site timezone
        // This ensures the calendar date is correct regardless of the input date's timezone context
        return this.timezoneService.formatDateInTimeZone(form.date, timezone, 'yyyy-MM-dd');
      } else {
        // Fallback to basic formatting
        return this.timezoneService.formatForRecurrence(form.date);
      }
    } else if (typeof form?.date === 'string') {
      return form?.date;
    } else {
      // Default to current time in site timezone
      if (timezone) {
        const now = this.timezoneService.now(timezone);
        return this.timezoneService.formatForRecurrence(now);
      } else {
        return format(startOfDay(new Date()), 'yyyy-MM-dd');
      }
    }
  }

  public async getRecurringBatchFromForm(data: {
    eventForm: EventFormValues;
    recurrenceId?: number;
    exdatesToMerge?;
    timezone: string;
  }): Promise<{
    form;
    dates: Date[];
  }> {
    const completeRecurrenceRecipe = this.finalizeRecurrenceDetailsToSave(
      data.eventForm.timing,
      data.exdatesToMerge,
      data.timezone,
    );
    // Ask API to expand dates in site timezone to avoid client TZ drift
    const previewUrl = `${environment.apiv3Url}/recurrence/preview`;
    const previewBody = {
      recurrence: completeRecurrenceRecipe,
      tzid: data.timezone,
    } as any;
    const preview = await lastValueFrom(this.http.post<{ dates: string[] }>(previewUrl, previewBody));
    let newRecurrenceId = data?.recurrenceId;
    if (!data?.recurrenceId) {
      const response = await this.saveRecurrence(
        this.ensureByweekdayAsStrings({
          ...completeRecurrenceRecipe,
          until:
            // hate using any here, but convoluted code made me do it - kd
            (data.eventForm.timing?.recurrenceRecipe as any)?.stopCondition ===
            'never'
              ? null
              : completeRecurrenceRecipe.until,
        }),
      );
      newRecurrenceId = response.id;
    }

    return {
      form: {
        ...data.eventForm.timing,
        ...data.eventForm.details,
        recurrenceId: newRecurrenceId ? newRecurrenceId : null,
      },
      dates: (preview?.dates || []) as any,
    };
  }

  public testForNewRecurrenceStart(
    savedRecurrence: RecurrenceDto,
    newTimingValues,
  ): boolean {
    // For existing recurring events, only consider it a "new start" if the user
    // actually intends to change the start date. Don't trigger on form initialization
    // or when editing different instances of the same series.

    // Check if the form date is actually different from the original recurrence dtstart
    const formDateString =
      typeof newTimingValues.date === 'string'
        ? newTimingValues.date
        : format(startOfDay(newTimingValues.date), 'yyyy-MM-dd');

    // If the form date matches the original recurrence dtstart, user hasn't changed start date
    const isActualStartDateChange = savedRecurrence?.dtstart !== formDateString;

    return isActualStartDateChange;
  }

  public testForRecurrenceEquality(
    savedRecurrence,
    newRecurrenceRecipe,
  ): boolean {
    const saved = {
      byweekday:
        savedRecurrence?.byweekday && savedRecurrence.byweekday.length > 0
          ? savedRecurrence.byweekday.map((string) => Number(string))
          : null,
      count: savedRecurrence?.count ? savedRecurrence.count : null,
      until: savedRecurrence?.until ? savedRecurrence.until : null,
      dtstart: savedRecurrence?.dtstart ? savedRecurrence.dtstart : null,
      exdates: savedRecurrence?.exdates ? savedRecurrence.exdates : null,
      freq: savedRecurrence?.freq ? savedRecurrence.freq : null,
      interval: savedRecurrence?.interval ? savedRecurrence.interval : null,
      nbyweekday: savedRecurrence?.nbyweekday
        ? savedRecurrence.nbyweekday
        : null,
    };

    const newRecurrence = {
      ...newRecurrenceRecipe,
      byweekday:
        newRecurrenceRecipe?.byweekday &&
        newRecurrenceRecipe.byweekday.length > 0
          ? newRecurrenceRecipe.byweekday
          : null,
    };

    return isEqual(saved, newRecurrence);
  }

  public getWeekdayString(day: Weekday | number): string {
    let dayString = '';
    let switchValue;
    if (typeof day === 'number') {
      switchValue = day;
    } else if (day.weekday) {
      switchValue = day.weekday;
    }
    switch (switchValue) {
      case RRule.MO.weekday:
        dayString = 'MO';
        break;
      case RRule.TU.weekday:
        dayString = 'TU';
        break;
      case RRule.WE.weekday:
        dayString = 'WE';
        break;
      case RRule.TH.weekday:
        dayString = 'TH';
        break;
      case RRule.FR.weekday:
        dayString = 'FR';
        break;
      case RRule.SA.weekday:
        dayString = 'SA';
        break;
      case RRule.SU.weekday:
        dayString = 'SU';
        break;
    }
    return dayString;
  }

  public getWeekdayFromString(day: string): Weekday {
    let weekday;
    switch (day) {
      case 'MO':
        weekday = RRule.MO;
        break;
      case 'TU':
        weekday = RRule.TU;
        break;
      case 'WE':
        weekday = RRule.WE;
        break;
      case 'TH':
        weekday = RRule.TH;
        break;
      case 'FR':
        weekday = RRule.FR;
        break;
      case 'SA':
        weekday = RRule.SA;
        break;
      case 'SU':
        weekday = RRule.SU;
        break;
    }
    return weekday;
  }

  public getWeekdayIntegerFromString(day: string): string {
    let dayInteger;
    switch (day) {
      case 'MO':
        dayInteger = RRule.MO.weekday;
        break;
      case 'TU':
        dayInteger = RRule.TU.weekday;
        break;
      case 'WE':
        dayInteger = RRule.WE.weekday;
        break;
      case 'TH':
        dayInteger = RRule.TH.weekday;
        break;
      case 'FR':
        dayInteger = RRule.FR.weekday;
        break;
      case 'SA':
        dayInteger = RRule.SA.weekday;
        break;
      case 'SU':
        dayInteger = RRule.SU.weekday;
        break;
    }
    return dayInteger;
  }

  public getWeekdayStringArray(days: Weekday[]): string[] {
    const dayString = [];
    days.forEach((day: Weekday, index) => {
      switch (day.weekday) {
        case RRule.MO.weekday:
          dayString.push('MO');
          break;
        case RRule.TU.weekday:
          dayString.push('TU');
          break;
        case RRule.WE.weekday:
          dayString.push('WE');
          break;
        case RRule.TH.weekday:
          dayString.push('TH');
          break;
        case RRule.FR.weekday:
          dayString.push('FR');
          break;
        case RRule.SA.weekday:
          dayString.push('SA');
          break;
        case RRule.SU.weekday:
          dayString.push('SU');
          break;
      }
    });
    return dayString;
  }
}
